<script lang="ts">
// import axios from "axios";
import { defineComponent, PropType } from "vue";
import { $vfm } from "vue-final-modal";
import { saveNewStructure, updateStructure } from "@api/roster";
import LoadPresavedToast from "./util/LoadPresavedToast.vue";
import { createToast, withProps } from "mosha-vue-toastify";
import "mosha-vue-toastify/dist/style.css";
import type { RosterType } from "@interfaces/roster_type";
import type { StructureType } from "@interfaces/structure_type";
import type { UnitType } from "@interfaces/unit_type";
import type { HeroType } from "@interfaces/hero_type";
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: "SelectStructure",
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true,
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true,
    },
  },
  data() {
    return {
      step: this.roster.id || this.structure.hero ? "selectUnit" : "selectDomain",
      structureChanged: this.roster.id ? false : true,
      /** When editing, ignore watch until initial load (factions/hero_details) has settled so the button stays disabled */
      watchForChangesEnabled: !this.roster.id,
      isRestoringDraft: false,
      // DISABLED: autosaved draft for locale switch / reload restore — may re-enable later
      // presaved_roster: localStorage.getItem("roster_draft")
      //   ? JSON.parse(localStorage.getItem("roster_draft")!)
      //   : false,
      presaved_roster: false,
      // Legacy key: used when unauth user tries to save and gets redirected to login
      presaved_auth_roster: localStorage.getItem("roster")
        ? JSON.parse(localStorage.getItem("roster")!)
        : false,
      draftSaveTimer: null as ReturnType<typeof window.setTimeout> | null,
    };
  },
  computed: {
    next_step(): string {
      const stepFlow = {
        selectName: "selectFormat",
        selectFormat: "selectDomain",
        selectDomain: "selectFaction",
        selectFaction: "selectHero",
        selectHero: "selectUnit",
        selectUnit: "selectUnit",
      };
      return stepFlow[this.step as keyof typeof stepFlow] || "selectUnit";
    },
  },
  watch: {
    structure: {
      handler(): void {
        if (this.watchForChangesEnabled) this.structureChanged = true;
      },
      deep: true,
    },
    roster: {
      handler(): void {
        if (this.watchForChangesEnabled) this.structureChanged = true;
      },
      deep: true,
    },
  },
  methods: {
    setStep(step_name: string): void {
      this.step = step_name;
    },
    resumeStepFromStructure(): string {
      // Resume wizard step from already selected structure
      if (this.structure.hero) return "selectUnit";
      if (this.structure.faction) return "selectHero";
      if (this.structure.domain) return "selectFaction";
      return "selectDomain";
    },
    isStructureEmpty(): boolean {
      const units = Array.isArray(this.structure.units) ? this.structure.units : [];
      const spells = Array.isArray(this.structure.spells) ? this.structure.spells : [];
      const equipment = Array.isArray(this.structure.equipment) ? this.structure.equipment : [];

      return !this.structure.domain && !this.structure.faction && !this.structure.hero &&
        units.length === 0 && spells.length === 0 && equipment.length === 0;
    },
    // DISABLED: restore draft after locale switch / reload — may re-enable later
    // async applyDraft(draft: any): Promise<void> {
    //   if (!draft?.structure) return;
    //
    //   this.isRestoringDraft = true;
    //   try {
    //     const draftStructure = draft.structure;
    //
    //     // Rehydrate draft entities (domain/faction/hero + nested lists) from the server in the *current* locale.
    //     let localizedDomain: any | null = null;
    //     let localizedFaction: any | null = null;
    //     let localizedHero: any | null = null;
    //
    //     try {
    //       const domains = await loadDomains();
    //       const wantedDomainId = draftStructure?.domain?.id;
    //       const wantedDomainTech = draftStructure?.domain?.tech_name;
    //       localizedDomain =
    //         domains.find((d: any) => (wantedDomainId != null && d.id === wantedDomainId)) ||
    //         domains.find((d: any) => (wantedDomainTech && d.tech_name === wantedDomainTech)) ||
    //         null;
    //
    //       const wantedFactionId = draftStructure?.faction?.id;
    //       const wantedFactionTech = draftStructure?.faction?.tech_name;
    //       localizedFaction =
    //         localizedDomain?.available_factions?.find((f: any) => (wantedFactionId != null && f.id === wantedFactionId)) ||
    //         localizedDomain?.available_factions?.find((f: any) => (wantedFactionTech && f.tech_name === wantedFactionTech)) ||
    //         null;
    //
    //       const wantedHeroId = draftStructure?.hero?.id;
    //       const wantedHeroTech = draftStructure?.hero?.tech_name;
    //       localizedHero =
    //         localizedFaction?.available_heroes?.find((h: any) => (wantedHeroId != null && h.id === wantedHeroId)) ||
    //         localizedFaction?.available_heroes?.find((h: any) => (wantedHeroTech && h.tech_name === wantedHeroTech)) ||
    //         null;
    //     } catch (e) {
    //       console.warn("Failed to rehydrate roster draft for current locale:", e);
    //     }
    //
    //     this.structure.domain = localizedDomain || draftStructure.domain;
    //     this.structure.faction = localizedFaction || draftStructure.faction;
    //     this.structure.hero = localizedHero || draftStructure.hero;
    //
    //     const hero = this.structure.hero as any;
    //     const availableUnits = Array.isArray(hero?.available_units) ? hero.available_units : [];
    //     const availableSpells = Array.isArray(hero?.available_spells) ? hero.available_spells : [];
    //     const availableEquipment = Array.isArray(hero?.available_equipment) ? hero.available_equipment : [];
    //
    //     const draftUnits = Array.isArray(draftStructure.units) ? draftStructure.units : [];
    //     const draftSpells = Array.isArray(draftStructure.spells) ? draftStructure.spells : [];
    //     const draftEquipment = Array.isArray(draftStructure.equipment) ? draftStructure.equipment : [];
    //
    //     this.structure.units = draftUnits
    //       .map((u: any) => {
    //         const base = availableUnits.find((au: any) => au.tech_name === u.tech_name) || u;
    //         const quantity = typeof u.quantity === "number" ? u.quantity : (typeof base.quantity === "number" ? base.quantity : 1);
    //         return { ...base, quantity };
    //       })
    //       .filter((u: any) => u && u.tech_name);
    //
    //     this.structure.spells = draftSpells
    //       .map((s: any) => {
    //         const base = availableSpells.find((as: any) => as.tech_name === s.tech_name) || s;
    //         const quantity = typeof s.quantity === "number" ? s.quantity : (typeof base.quantity === "number" ? base.quantity : 1);
    //         return { ...base, quantity };
    //       })
    //       .filter((s: any) => s && s.tech_name);
    //
    //     this.structure.equipment = draftEquipment
    //       .map((e: any) => {
    //         const base = availableEquipment.find((ae: any) => ae.tech_name === e.tech_name) || e;
    //         const quantity = typeof e.quantity === "number" ? e.quantity : (typeof base.quantity === "number" ? base.quantity : 1);
    //         return { ...base, quantity };
    //       })
    //       .filter((e: any) => e && e.tech_name);
    //
    //     const heroCost = hero?.point_cost || 0;
    //     const unitsCost = this.structure.units.reduce((sum: number, u: any) => sum + (u.point_cost || 0) * (u.quantity || 0), 0);
    //     const equipmentCost = this.structure.equipment.reduce((sum: number, e: any) => sum + (e.point_cost || 0) * (e.quantity || 0), 0);
    //     this.structure.point_cost = heroCost + unitsCost + equipmentCost;
    //
    //     this.structure.max_points = draftStructure.max_points ?? draftStructure.maxPoints ?? this.structure.max_points;
    //
    //     this.roster.name = draft.name;
    //     this.roster.points_left = draft.points_left;
    //     this.roster.points_overall = draft.points_overall;
    //     this.roster.roster_format_id = draft.roster_format_id;
    //
    //     this.setStep(this.resumeStepFromStructure());
    //   } finally {
    //     this.isRestoringDraft = false;
    //   }
    // },
    // saveDraftNow(): void {
    //   if (this.roster.id !== null && this.roster.id !== undefined) return;
    //   const draft = { ...this.roster, structure: this.structure };
    //   try {
    //     localStorage.setItem("roster_draft", JSON.stringify(draft));
    //   } catch (e) {
    //     console.warn("Failed to autosave roster draft:", e);
    //   }
    // },
    // queueDraftSave(): void {
    //   if (this.isRestoringDraft) return;
    //   if (this.roster.id !== null && this.roster.id !== undefined) {
    //     try {
    //       localStorage.removeItem("roster_draft");
    //     } catch (_) {}
    //     return;
    //   }
    //   if (this.draftSaveTimer) {
    //     window.clearTimeout(this.draftSaveTimer);
    //   }
    //   this.draftSaveTimer = window.setTimeout(() => {
    //     this.saveDraftNow();
    //     this.draftSaveTimer = null;
    //   }, 600);
    // },
    async saveStructure(): Promise<void> {
      if (this.roster.id === null) {
        const response = await saveNewStructure(this.roster, this.structure);
        this.structureChanged = false;
        if (response) {
          // DISABLED: clear draft after save — may re-enable later
          // try {
          //   localStorage.removeItem("roster_draft");
          // } catch (_) {}
          window.location.href = response.url;
        }
      } else {
        await updateStructure(this.roster.id, this.roster, this.structure);
        this.structureChanged = false;
      }
    },
    openUnitModal(entity: UnitType | HeroType): void {
      $vfm.show({
        component: "UnitModal",
        bind: {
          name: "UnitModal",
          imgSrc: entity.card_image_path,
          backImgSrc: entity.card_back_image_path,
          imgSrc2: entity.card_image_path_2 || "",
          backImgSrc2: entity.card_back_image_path_2 || "",
        },
        on: {
          cancel(close: () => void) {
            close();
          },
        },
      });
    },
  },
  async mounted() {
    if (this.roster.id) {
      setTimeout(() => { this.watchForChangesEnabled = true; }, 800);
    }
    // DISABLED: flush draft before leaving (locale switch, reload) — may re-enable later
    // window.addEventListener('beforeunload', () => {
    //   try {
    //     this.saveDraftNow();
    //   } catch (_) {}
    // });
    // document.addEventListener('turbolinks:before-visit', () => {
    //   try {
    //     this.saveDraftNow();
    //   } catch (_) {}
    // });

    // DISABLED: restore draft after locale switch / reload — may re-enable later
    // const isReloadNavigation = (): boolean => { ... };
    // const shouldRestoreDraft = (): boolean => { ... };

    // If there is a legacy saved roster (unauth save flow), show the toast offering to load it.
    if (
      this.presaved_auth_roster &&
      this.isStructureEmpty() &&
      (this.roster.id === null || this.roster.id === undefined)
    ) {
      createToast(
        withProps(LoadPresavedToast, {
          roster: this.roster,
          structure: this.structure,
          i18n: this.t
        }),
        {
          position: "bottom-center",
          timeout: 6000,
          hideProgressBar: false,
          onClose: () => {
            this.setStep(this.resumeStepFromStructure());
          }
        }
      );
      return;
    }

    // DISABLED: silent restore of roster_draft (locale/reload) — may re-enable later
    // if (
    //   this.presaved_roster &&
    //   this.isStructureEmpty() &&
    //   (this.roster.id === null || this.roster.id === undefined) &&
    //   shouldRestoreDraft()
    // ) {
    //   await this.applyDraft(this.presaved_roster);
    //   try {
    //     sessionStorage.removeItem('roster_draft_restore_once');
    //     sessionStorage.removeItem('roster_draft_clear_once');
    //   } catch (_) {}
    // } else {
    //   try {
    //     sessionStorage.removeItem('roster_draft_clear_once');
    //   } catch (_) {}
    // }
  },
  setup() {
    const i18n = useI18n();
    return {
      t: i18n.t
    };
  }
});
</script>

<template>
  <Teleport to="header#breadcrumbs">
    <breadcrumbs
      :step="step"
      :roster="roster"
      :structure="structure"
      @select-step="setStep"
    ></breadcrumbs>
  </Teleport>

  <template v-if="['selectUnit', 'selectSpell', 'selectEquipment'].includes(step)">
    <Teleport to="#roster_stat">
      <current-build
        :roster="roster"
        :structure="structure"
        @changeName="structureChanged = true"
      ></current-build>
    </Teleport>

    <current-units
      @open-unit-modal="openUnitModal"
      :roster="roster"
      :structure="structure"
    >
    </current-units>
  </template>

 <div class="roster-builder">
   <select-domain
     v-if="step == 'selectDomain'"
     :roster="roster"
     :structure="structure"
     @select-step="setStep"
   />
   <select-faction
     v-if="step == 'selectFaction'"
     :roster="roster"
     :structure="structure"
     @select-step="setStep"
   />
   <select-hero
     v-if="step == 'selectHero'"
     :roster="roster"
     :structure="structure"
     @select-step="setStep"
   />
   <select-unit
     v-if="step == 'selectUnit'"
     :roster="roster"
     :structure="structure"
     :multiplier="1"
     @open-unit-modal="openUnitModal"
   />
  </div>

  <template v-if="step === 'selectUnit'">
    <div class="fixed fixed_bottom">
      <button
        class="button button_wide"
        :disabled="structureChanged === false"
        @click.prevent.stop="saveStructure"
      >
        {{ structureChanged === true ? t('structure.save') : t('structure.saved') }}
      </button>
    </div>
  </template>

  <Teleport to="body">
    <modals-container></modals-container>
  </Teleport>
</template>
